/**
 * Order utility functions
 */

import type { Order } from '~/features/orders/types-order';

/**
 * Extracts the city from an order, with fallback logic
 * @param order - The order object
 * @returns The city name or a fallback string
 */
export function getOrderCity(order?: Order | null): string {
  if (!order) {
    return '—';
  }
  
  // Prefer ship_to_city, fallback to empty string
  return order.ship_to_city || '—';
}

export type PhoneDisplaySource = 'phone' | 'ai_inferred' | 'raw' | null;

/**
 * Display phone for order: phone if it has data, else ai_inferred_phone, else raw_phone.
 * Empty string is treated as "no data" so we fall through to the next option.
 */
export function getOrderDisplayPhone(order?: Order | null): string {
  if (!order) return '';
  const candidates = [order.phone, order.ai_inferred_phone, order.raw_phone];
  for (const c of candidates) {
    const s = typeof c === 'string' ? c.trim() : '';
    if (s) return s;
  }
  return '';
}

/**
 * Which source was used for the primary display phone (for showing raw/AI icon).
 */
export function getOrderDisplayPhoneSource(order?: Order | null): PhoneDisplaySource {
  if (!order) return null;
  if (typeof order.phone === 'string' && order.phone.trim()) return 'phone';
  if (typeof order.ai_inferred_phone === 'string' && order.ai_inferred_phone.trim()) return 'ai_inferred';
  if (typeof order.raw_phone === 'string' && order.raw_phone.trim()) return 'raw';
  return null;
}

/**
 * Display alt phone: phone_secondary if it has data, else ai_inferred_phone_secondary, else raw_phone_secondary.
 */
export function getOrderDisplayPhoneSecondary(order?: Order | null): string {
  if (!order) return '';
  const candidates = [order.phone_secondary, order.ai_inferred_phone_secondary, order.raw_phone_secondary];
  for (const c of candidates) {
    const s = typeof c === 'string' ? c.trim() : '';
    if (s) return s;
  }
  return '';
}

/**
 * Which source was used for the alt display phone (for showing raw/AI icon).
 */
export function getOrderDisplayPhoneSecondarySource(order?: Order | null): PhoneDisplaySource {
  if (!order) return null;
  if (typeof order.phone_secondary === 'string' && order.phone_secondary.trim()) return 'phone';
  if (typeof order.ai_inferred_phone_secondary === 'string' && order.ai_inferred_phone_secondary.trim()) return 'ai_inferred';
  if (typeof order.raw_phone_secondary === 'string' && order.raw_phone_secondary.trim()) return 'raw';
  return null;
}
